import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import { setScrollbarWidthProperty } from './utils/styleHelpers'
import { fetchPage as fetchPageApi } from '../api'
import { useConfigStore } from './config'
import { useSessionStore } from './session'

import type { Ref } from 'vue'
import type { Brand } from '@xcel-next/core/enums'
import type { ComposedPage } from '@xcel-next/composer-schema'
import type { FetchPageContext } from '../types'

type PageData = ComposedPage | null

type PageResponse = {
  pageData: PageData,
  brand: Brand,
  status: number,
  imageBrand?: Brand
}

export const usePageStore = defineStore('page', () => {
  // Stores
  const configStore = useConfigStore()
  const sessionStore = useSessionStore()

  // State
  const pageData = ref<PageData>(null)
  const isPreview = ref(false) // for the-gui preview
  const brand = ref<Brand | undefined>()
  const imageBrand = ref('')
  const status = ref<number | null>(null)
  const isPrintPreview = ref(false)
  const viewportMetaModified = ref(false)
  const disclaimerOpen = ref(false)

  // getters
  const isArticleOrArticleVersion = computed(() => {
    return ['article', 'articleversion'].includes(pageData.value?.pageMeta?.pageType ?? '')
  })

  const hasPreviewToken = computed(() => {
    return !import.meta.env.SSR && /[?&]previewToken=(?:\S)/.test(window.location.search)
  })

  // setters
  // optional and only used by the-gui preview to ensure rendering components are rendered correctly in `AssetPreview.vue` and `ParagraphsPreview.vue`
  const setIsPreview = (newValue: boolean) => {
    isPreview.value = newValue
  }

  const setBrand = (newBrand: Brand) => {
    brand.value = newBrand
  }

  // the-gui preview never calls `setPage()`, so `pageData` would otherwise stay null and these setters no-op
  const ensurePageData = () => {
    pageData.value ??= {} as ComposedPage
    return pageData.value
  }

  const setPageMetaChannelContext = ({ nodeId, url }: { nodeId: number | undefined, url: string | undefined }) => {
    const page = ensurePageData()
    page.pageMeta ??= {}
    // written together in one call (not as two separate setters) so an interleaved concurrent preview can't
    // observe/leave behind a mixed nodeId/url pair - see setPageMetaChannelContext usage in the-gui
    page.pageMeta.nodeId = nodeId
    page.pageMeta.url = url
  }

  // Actions
  const fetchPage = async (context: FetchPageContext) => {
    // TODO: Defeer setting to avoid flashing loading on every page change?
    const proxyServiceURL = !import.meta.env.SSR ? configStore.rsConfig.proxyServiceUrl : undefined
    const sweepstakesActive = !!(configStore?.rsConfig.sweepstakesActive)
    const { pageData: fetchedPageData, brand: fetchedBrand, status: fetchedStatus, imageBrand: fetchedImageBrand } = await fetchPageApi({
      ...context,
      token: sessionStore.token,
      sweepstakesActive
    }, proxyServiceURL)
    setPage({ pageData: fetchedPageData as PageData, brand: fetchedBrand, status: fetchedStatus, imageBrand: fetchedImageBrand })
  }

  const appMounted = async () => {
    setScrollbarWidthProperty()
    sessionStore.watchLocalStorage()

    if (!sessionStore.token) {
      return sessionStore.clearUserToken()
    }

    sessionStore.userInit()
  }

  const setViewportMetaModified = (newValue: boolean) => {
    viewportMetaModified.value = newValue
  }

  const setPage = (newPage: PageResponse) => { // newPage = { pageData, brand, status, imageBrand, noScripts }
    pageData.value = newPage.pageData
    brand.value = newPage.brand
    imageBrand.value = newPage.imageBrand ?? ''
    status.value = newPage.status
  }

  const setStatus = (newStatus: number | null) => {
    status.value = newStatus
  }

  const setPrintPreview = (newValue: boolean) => {
    isPrintPreview.value = newValue
  }

  const toggleAffiliateDisclaimer = () => {
    disclaimerOpen.value = !disclaimerOpen.value
  }

  return {
    pageData,
    isPreview,
    brandFromStore: brand as Ref<Brand>,
    imageBrand,
    viewportMetaModified,
    isPrintPreview,
    isArticleOrArticleVersion,
    hasPreviewToken,
    status,
    disclaimerOpen,
    setIsPreview,
    setBrandInStore: setBrand,
    setPageMetaChannelContext,
    fetchPage,
    appMounted,
    setPage,
    setViewportMetaModified,
    setStatus,
    setPrintPreview,
    toggleAffiliateDisclaimer
  }
})
